import { useCallback } from 'react';

import { useNavigate } from 'react-router';
import { useWithdrawQuery } from '@/hooks/webapi/client/useWithdrawQuery';
import * as R from '@/Router/routes';
import { Application } from '@/api/getClientApplication';
import { getClientLoansLatest } from '@/api/getClientLoansLatest';
import useSmartlook from '@/hooks/useSmartlook';
import getClient from '@/api/getClient';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import IdentifyEvent from '@/hooks/context/EventCollector/events/IdentifyEvent';
import useCrossDomainLoginRedirect from '@/pages/Login/useCrossDomainRedirect';
import { useKeycloakStore } from '@/hooks/zustandStore';
import useCrossDomainLegacyRedirect from '@/pages/Login/LegacyLogin/useCrossDomainLegacyRedirect';
import { resolveIsLegacyLogin } from '@/utils/fetchData.ts';
import useDashboardRoute from '../useDashboardRoute';

export const useResolutionResolverAuthorization = () => {
  const navigate = useNavigate();
  const { identify } = useEventCollector();
  const { identifySmartlook } = useSmartlook();
  const { refetch: refetchWithdraw } = useWithdrawQuery();
  const { crossDomainRedirect } = useCrossDomainLoginRedirect();
  const { crossDomainRedirect: crossLegacyDomainRedirect } =
    useCrossDomainLegacyRedirect();
  const { getDashboardRoute } = useDashboardRoute();

  const isLegacyLogin = useKeycloakStore((state) => state.isLegacyLogin);

  const resolveAuthorization = useCallback(
    async (application: Application) => {
      const client = await getClient();
      const resolvedIsLegacyLogin = await resolveIsLegacyLogin();

      let latestLoan = null;
      try {
        latestLoan = await getClientLoansLatest();
      } catch {
        // No need to do anything
      }

      // Only applies to clients with an open loan
      const withdrawFetched = latestLoan && (await refetchWithdraw())?.data;

      identify(
        new IdentifyEvent({
          client_loan: latestLoan,
          client,
        }),
      );
      identifySmartlook(client?.number);

      const {
        expired,
        confirmed,
        resolution,
        resolutionDetail,
        resolutionMessage,
        riskEvaluationStarted,
      } = application;

      const dashboardRoute = getDashboardRoute(latestLoan, withdrawFetched);

      // If client has not finished registration process
      if (
        client &&
        (client.status === 'REGISTERED' || client.status === 'VALIDATED')
      ) {
        // Take user to registration app
        if (resolvedIsLegacyLogin) {
          await crossLegacyDomainRedirect();
        } else {
          await crossDomainRedirect();
        }
        return;
      }

      if (!riskEvaluationStarted) {
        navigate(latestLoan ? R.ACTIVE_LOAN : R.REPEATED_LOAN);
        return;
      }

      if (expired) {
        navigate(dashboardRoute);
        return;
      }

      if (resolution === 'REJECTED' && resolutionDetail === 'UPSALE_OFFER') {
        navigate(R.NEW_OFFER_INCREASED);
        return;
      }

      if (resolution === 'REJECTED' && resolutionDetail === 'NEW_OFFER') {
        navigate(R.NEW_OFFER_DECREASED);
        return;
      }

      if (resolution === 'REJECTED') {
        navigate(R.LOAN_REJECTED);
        return;
      }

      if (
        resolution === 'MANUAL' &&
        resolutionDetail === 'INSUFFICIENT_PSD2_INCOME'
      ) {
        if (resolutionMessage === 'Insufficient_PSD2_Income') {
          navigate(R.ADJUST_INCOME);
          return;
        }

        navigate(R.DOCUMENT_UPLOAD);
        return;
      }

      if (resolution === 'MANUAL' && resolutionDetail === 'SOLVENCY') {
        navigate(R.PSD2_BANKS);
        return;
      }

      if (resolution === 'MANUAL') {
        navigate(R.DOCUMENT_UPLOAD_PENDING);
        return;
      }

      if (resolution === 'APPROVED' && !confirmed) {
        navigate(R.LOAN_CONFIRM);
        return;
      }

      navigate(dashboardRoute);
    },
    [isLegacyLogin],
  );

  return { resolveAuthorization };
};

export default useResolutionResolverAuthorization;
